import CalendarMonth from "@mui/icons-material/CalendarMonth";
import CalendarIcon from "@mui/icons-material/CalendarToday";
import Delete from "@mui/icons-material/Delete";
import ListIcon from "@mui/icons-material/FormatListBulleted";
import {
  Box,
  Button,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { ExportDataButton } from "components/ExportDataButton";
import { CellText } from "components/Omnisearch/helpComponents/CellText";
import {
  OmnisearchCommandButtonsContext,
  OmnisearchDataDisplay,
} from "components/OmnisearchDataDisplay";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TagsDisplay } from "components/TagsDisplay";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { getFullName } from "encamp-shared/src/utils/name";
import {
  EventMutationScope,
  Frequency,
  Permission,
  TaskStatus,
  TasksQuery,
} from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { DateTime } from "luxon";
import { OmnisearchProvider } from "providers/OmnisearchContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { prettyPrintDateMed } from "util/dates";

import { AddTaskDialog } from "./AddTaskDialog";
import { getDefaultTaskSearch } from "./defaultSearch";
import { EditTaskDialog } from "./EditTaskDialog";
import RecurrenceScopeRadioDialog from "./RecurrenceScopeRadioDialog";
import { TASKS_COUNT_QUERY, TASKS_QUERY } from "./schema";
import { taskStatusToChipSx } from "./statusStyles";
import { SuggestedTasksGrid } from "./SuggestedTasks";
import { TaskCalendarSubscriptionDialog } from "./TaskCalendarSubscriptionDialog";
import { TaskFilterChips, TaskFilterType } from "./TaskFilterChips";
import { TasksCalendar } from "./TasksCalendar";
import useDeleteTaskMutation from "./useDeleteTask";
import { useTaskFilterChips } from "./useTaskFilterChips";
import { buildBeforeAfterSearchString } from "./util.buildSearchString";
import { taskStatusToLabel } from "./util.labels";

export type Row = TasksQuery["virtualEvents"]["items"][0];

type ViewMode = "list" | "calendar";

const defaultDeleteTaskState = {
  open: false,
  recurring: false,
  virtualEvent: null,
};
export const Tasks = () => {
  const { tenantId } = useParams();
  const alerts = useAlerts();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { featureFlags } = useFeatureFlags();

  // Suggested Tasks Disabled: keep code, but prevent UI + queries from running.
  // const isSuggestedTasksEnabled = featureFlags[FeatureFlagKey.SuggestedTasks];
  const isSuggestedTasksEnabled = false;

  const { activeFilter } = useTaskFilterChips();
  const isCalendarICSEnabled = featureFlags[FeatureFlagKey.TaskCalendarICSIntegration];

  const { hasTaskWritePermissionForFacilities, hasPermissions } = useAuthorization();
  const hasAllFacilitiesPermission = hasPermissions([Permission.ReadAllFacility]);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openCalendarSubscription, setOpenCalendarSubscription] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("list");

  const [calendarRange, setCalendarRange] = useState<string>(() =>
    buildBeforeAfterSearchString(DateTime.local())
  );

  useEffect(() => {
    setCalendarRange(buildBeforeAfterSearchString(DateTime.local()));
  }, []);

  const onChangeViewMode = useCallback(
    (event: React.MouseEvent<HTMLElement>, newViewMode: ViewMode) => {
      setViewMode(newViewMode);
    },
    []
  );

  const [deleteTaskState, setDeleteTaskState] = useState<{
    open: boolean;
    recurring: boolean;
    virtualEvent: Pick<Row, "taskId" | "deadline" | "title"> | null;
  }>(defaultDeleteTaskState);
  const resetDeleteTaskState = () => setDeleteTaskState(defaultDeleteTaskState);

  const { user } = useCurrentUser();
  const canAddTask = useMemo(() => {
    return hasPermissions([Permission.WriteAllFacility]) || (user?.facilities.length ?? 0) > 0;
  }, [hasPermissions, user?.facilities.length]);

  const [deleteTask, { loading: deleteTaskLoading }] = useDeleteTaskMutation();

  const {
    selectedId: virtualEventId,
    pushIdToUrl: pushVirtualEventIdToUrl,
    popIdFromUrl: removeVirtualEventIdFromUrl,
  } = useSelectedIdInUrl("virtualEventId");

  const onRowEdit = (row: Row) => {
    if (!hasTaskWritePermissionForFacilities(row.facilities?.map((f) => f.id) ?? [])) {
      return; // no op because the user doesn't have permissions
    }

    pushVirtualEventIdToUrl(row.id);
  };

  const onDeleteTask = useCallback(
    (scope: EventMutationScope) => {
      if (deleteTaskState.virtualEvent) {
        deleteTask({
          variables: {
            input: {
              taskId: deleteTaskState.virtualEvent.taskId,
              date: deleteTaskState.virtualEvent.deadline,
              eventMutationScope: scope,
            },
          },
          onCompleted: () => {
            resetDeleteTaskState();
            alerts.success(`Event deleted`);
          },
          onError: (error) => {
            alerts.error(`Error deleting event: ${error.message}`);
          },
          refetchQueries: [TASKS_QUERY, TASKS_COUNT_QUERY],
        });
      }
    },
    [deleteTask, deleteTaskState.virtualEvent, alerts]
  );

  useBreadcrumb([{ label: "Tasks" }]);

  const columns: OmnisearchGridColDef<Row>[] = [
    {
      field: "deadline",
      headerName: "Deadline",
      sortable: true,
      flex: 0.7,
      renderCell: ({ value }) => {
        return <CellText variant="body2">{prettyPrintDateMed(value)}</CellText>;
      },
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1.5,
      sortable: true,
      renderCell: ({ row: { title } }) => {
        return <CellText variant="body2">{title}</CellText>;
      },
    },
    {
      field: "facilities",
      headerName: "Facilities",
      flex: 1.5,
      sortable: true,
      renderCell({ row }) {
        return (
          <Stack
            spacing={0}
            style={{
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
              height: "100%",
              width: "100%",
              alignItems: "start",
              overflow: "hidden",
            }}
          >
            {(row.facilities ?? []).map((facility) => (
              <Typography
                key={facility.id}
                variant="body2"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                <Tooltip title={facility.name}>
                  <span>{facility.name}</span>
                </Tooltip>
              </Typography>
            ))}
          </Stack>
        );
      },
    },
    {
      field: "taskStatus",
      headerName: "Status",
      flex: 0.7,
      sortable: true,
      renderCell({ row: { taskStatus } }) {
        const stylingBlock = taskStatusToChipSx(taskStatus, theme);
        return <Chip label={taskStatusToLabel(taskStatus)} sx={stylingBlock} size="small" />;
      },
      filterKeyType: "enumAutocomplete",
      enumValues: Object.values(TaskStatus),
      enumPresentationFunction: (enumValue) => taskStatusToLabel(enumValue),
    },

    {
      field: "assignee",
      headerName: "Assignee",
      sortable: true,
      flex: 1,
      valueGetter(_, row) {
        return getFullName(row.assignee?.person) || row.assignee?.email;
      },
      renderCell: ({ value }) => {
        return <CellText variant="body2">{value}</CellText>;
      },
    },
    {
      field: "tags",
      headerName: "Tags",
      flex: 0.7,
      filterKeyType: "taskTags",
      renderCell: ({ row }) => (
        <TagsDisplay tags={row.tags?.map((tag) => ({ id: tag.id, name: tag.name })) ?? []} />
      ),
    },
    {
      field: "actions",
      type: "actions",
      sortable: false,
      align: "right",
      width: 30,
      getActions: ({ row }) => {
        const actions = [
          <Tooltip title="Delete Task" key="delete">
            <GridActionsCellItem
              onClick={() =>
                setDeleteTaskState({
                  open: true,
                  recurring: row.frequency !== Frequency.Once,
                  virtualEvent: row,
                })
              }
              label="Delete Task"
              icon={<Delete />}
            />
          </Tooltip>,
        ];

        return hasTaskWritePermissionForFacilities(row.facilities?.map((f) => f.id) ?? [])
          ? actions
          : [];
      },
    },
  ];

  const additionFilterColumns: FilterKey[] = [
    {
      key: "facility",
      header: "Facility Name",
      filterKeyType: "facility",
    },
    { key: "after", header: "Due After", filterKeyType: "date" },
    { key: "before", header: "Due Before", filterKeyType: "date" },
  ];

  const viewModeToggle = [
    <ToggleButtonGroup
      key="view-mode-toggle-button-group"
      value={viewMode}
      exclusive
      color="primary"
      size="large"
      onChange={onChangeViewMode}
    >
      Omnisearch
      <ToggleButton value="list">
        <ListIcon />
      </ToggleButton>
      <ToggleButton value="calendar">
        <CalendarIcon />
      </ToggleButton>
    </ToggleButtonGroup>,
  ];

  const commandButtons: (context: OmnisearchCommandButtonsContext) => React.ReactNode[] = ({
    search,
  }) => {
    const buttons = [
      <ExportDataButton
        key="export-tasks-button"
        tenantId={tenantId}
        exportType="TASKS"
        filter={{ search }}
        title="Export Tasks"
        fileName="tasks-export"
      />,
    ];

    // Calendar subscription button (if ICS integration is enabled)
    if (isCalendarICSEnabled && hasAllFacilitiesPermission) {
      buttons.push(
        <Tooltip
          title="Subscribe to tasks in your calendar app"
          key="calendar-subscription-tooltip"
        >
          <Button
            key="calendar-subscription-button"
            variant="outlined"
            startIcon={<CalendarMonth />}
            onClick={() => setOpenCalendarSubscription(true)}
          >
            Subscribe
          </Button>
        </Tooltip>
      );
    }

    // Add task button for users with permission
    if (canAddTask) {
      buttons.push(
        <Button key="add-task-button" variant="contained" onClick={() => setOpenAddModal(true)}>
          Add Task
        </Button>
      );
    }

    return buttons;
  };

  // Determine what to show based on filter
  const showSuggestedOnly = activeFilter === TaskFilterType.Suggested && isSuggestedTasksEnabled;

  const defaultSearch = getDefaultTaskSearch(tenantId, activeFilter);
  const defaultCalendarSearch = getDefaultTaskSearch(tenantId, TaskFilterType.AllButCanceled);

  return (
    <>
      <Box sx={{ pt: 3 }}>
        {isSuggestedTasksEnabled && <TaskFilterChips />}

        {/* Show only SuggestedTasksGrid when "suggested" filter is active */}
        {showSuggestedOnly ? (
          tenantId && <SuggestedTasksGrid tenantId={tenantId} />
        ) : (
          <>
            {viewMode === "list" ? (
              <OmnisearchDataGrid
                persistenceKey="tasks-table"
                noDataMessage="No tasks have been added to this organization yet."
                isURLDriven
                columns={columns}
                initialSortModel={[{ field: "deadline", sort: "asc" }]}
                dataQuery={TASKS_QUERY}
                defaultSearch={defaultSearch}
                getItems={(data) => data.virtualEvents.items}
                getCount={(data) => data.virtualEvents.count ?? 0}
                excludeFilterColumns={["deadline", "facilities"]}
                additionalFilterColumns={additionFilterColumns}
                isRowSelectable={() => false}
                onRowClick={({ row }) => onRowEdit(row)}
                startCommandButtons={viewModeToggle}
                commandButtons={commandButtons}
                initialPageSize={10}
                rowHeight={"auto"}
                columnVisibilityModel={{
                  ...(isMobile && {
                    facilities: false,
                    tags: false,
                  }),
                }}
              />
            ) : (
              <OmnisearchProvider>
                <OmnisearchDataDisplay
                  persistenceKey="tasks-calendar"
                  isURLDriven
                  columns={columns}
                  dataQuery={TASKS_QUERY}
                  dataQueryVariables={{ pageSize: undefined, page: undefined }}
                  defaultSearch={`${defaultCalendarSearch} ${calendarRange}`}
                  getItems={(data) => data.virtualEvents.items}
                  getCount={(data) => data.virtualEvents.count ?? 0}
                  excludeFilterColumns={["deadline", "facilities"]}
                  additionalFilterColumns={additionFilterColumns}
                  startCommandButtons={viewModeToggle}
                  commandButtons={commandButtons}
                  dataDisplay={({ items, loading }) => (
                    <TasksCalendar
                      loading={loading}
                      tasks={items}
                      onTaskClick={onRowEdit}
                      onRangeChange={setCalendarRange}
                    />
                  )}
                />
              </OmnisearchProvider>
            )}
          </>
        )}
      </Box>
      {openAddModal && <AddTaskDialog open={openAddModal} onClose={() => setOpenAddModal(false)} />}
      {openCalendarSubscription && (
        <TaskCalendarSubscriptionDialog
          open={openCalendarSubscription}
          onClose={() => setOpenCalendarSubscription(false)}
        />
      )}
      {virtualEventId && (
        <EditTaskDialog virtualEventId={virtualEventId} onClose={removeVirtualEventIdFromUrl} />
      )}
      {deleteTaskState.recurring && (
        <RecurrenceScopeRadioDialog
          action="Delete"
          open={deleteTaskState.open}
          onClose={resetDeleteTaskState}
          onSubmit={onDeleteTask}
          loading={deleteTaskLoading}
        />
      )}
      {deleteTaskState.open && !deleteTaskState.recurring && (
        <ConfirmDialog
          open={deleteTaskState.open}
          onClose={resetDeleteTaskState}
          onConfirm={() => onDeleteTask(EventMutationScope.This)}
          header="Are you sure you want to delete this task?"
          resources={[
            {
              resourceType: "Task",
              resourceName: deleteTaskState.virtualEvent?.title,
            },
          ]}
          loading={deleteTaskLoading}
        />
      )}
    </>
  );
};
